import type { TFunction } from 'i18next'

import type {
  Campaign,
  CampaignItemDetail,
  CampaignType,
  FoodOfferCampaign
} from '@/ducks/campaigns/campaigns.types'
import { formatPrice } from '@/utils/currency.utils'

export type CampaignDisplayStatus = 'active' | 'inactive' | 'expired'

const MS_PER_DAY = 24 * 60 * 60 * 1000

const isItemDiscount = (campaign: Campaign): campaign is FoodOfferCampaign =>
  campaign.campaignType === 'food-offer'

/** A flat ("100 DZD off") item discount. Rows from an older backend carry no type: percentage. */
export const isFlatItemDiscount = (campaign: Campaign): boolean =>
  isItemDiscount(campaign) && campaign.offerDiscountType === 'flat'

/** An item discount Yassir created (Admin Dashboard), whatever its type or funding. */
export const isInternalCampaign = (campaign: Campaign): boolean =>
  isItemDiscount(campaign) && campaign.creationSource === 'internal'

const isUnlimitedExpiryValue = (value: string | number): boolean => {
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'unlimited'
  }

  return false
}

const parseExpiryToTimestamp = (value: string | number | null | undefined): number | null => {
  if (value == null || value === '') return null

  if (typeof value === 'string' && isUnlimitedExpiryValue(value)) {
    return null
  }

  if (typeof value === 'number') {
    return value > 0 ? value : null
  }

  const numeric = Number(value)
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const hasUnlimitedExpiry = (
  ...values: Array<string | number | null | undefined>
): boolean => values.some(value => isUnlimitedExpiryValue(value as string | number))

export const getCampaignExpiryTimestamp = (campaign: Campaign): number | null => {
  if (campaign.campaignType === 'food-offer') {
    if (
      hasUnlimitedExpiry(
        campaign.validUntil,
        campaign.expiryDate,
        campaign.offerValidity
      )
    ) {
      return null
    }

    return (
      parseExpiryToTimestamp(campaign.validUntil) ??
      parseExpiryToTimestamp(campaign.expiryDate) ??
      parseExpiryToTimestamp(campaign.offerValidity)
    )
  }

  if (campaign.campaignType === 'cart_discount') {
    if (hasUnlimitedExpiry(campaign.expiryDate, campaign.expirationTime)) {
      return null
    }

    return (
      parseExpiryToTimestamp(campaign.expiryDate) ??
      parseExpiryToTimestamp(campaign.expirationTime)
    )
  }

  if (hasUnlimitedExpiry(campaign.expiryDate)) {
    return null
  }

  return parseExpiryToTimestamp(campaign.expiryDate)
}

export const isCampaignExpired = (campaign: Campaign, now = Date.now()): boolean => {
  const expiry = getCampaignExpiryTimestamp(campaign)
  if (expiry == null) return false
  return expiry < now
}

export const getCampaignDisplayStatus = (
  campaign: Campaign,
  now = Date.now()
): CampaignDisplayStatus => {
  if (isCampaignExpired(campaign, now)) return 'expired'
  if (campaign.status === 1) return 'active'
  return 'inactive'
}

export const getCampaignOfferLabel = (
  campaign: Campaign,
  t: TFunction
): string => getCampaignTypeLabel(campaign.campaignType, t)

export const getCampaignPercentageBadge = (
  campaign: Campaign,
  t: TFunction
): string | null => {
  if (campaign.campaignType === 'cart_discount' && campaign.amountPercentage != null) {
    return t('marketing.campaigns.percent-off', {
      value: campaign.amountPercentage
    })
  }

  if (campaign.campaignType === 'food-offer' && campaign.offerAmount != null) {
    return t('marketing.campaigns.percent-off', {
      value: campaign.offerAmount
    })
  }

  return null
}

export const getCampaignFoundedBy = (campaign: Campaign): string | null => {
  const record = campaign as Campaign & {
    fundedBy?: string | null
    founded_by?: string | null
  }
  const value = campaign.foundedBy ?? record.fundedBy ?? record.founded_by

  return value?.trim() || null
}

export const isYassirFundedCampaign = (campaign: Campaign): boolean =>
  getCampaignFoundedBy(campaign)?.toLowerCase() === 'yassir'

/**
 * The merchant can only view these: Yassir-funded campaigns, and (EXSR-8095) every item
 * discount Yassir created, whatever its funding.
 */
export const isReadOnlyCampaign = (campaign: Campaign): boolean =>
  isYassirFundedCampaign(campaign) || isInternalCampaign(campaign)

export const getFundedByLabel = (
  foundedBy: string | null,
  t: TFunction
): string | null => {
  if (!foundedBy?.trim()) return null
  return t('marketing.campaigns.funded-by', { name: foundedBy.trim() })
}

export const getCampaignFundedByLabel = (
  campaign: Campaign,
  t: TFunction
): string => {
  const foundedBy = getCampaignFoundedBy(campaign)
  if (!foundedBy) return '--'
  return t('marketing.campaigns.funded-by', { name: foundedBy })
}

export const getCampaignExpirationText = (
  campaign: Campaign,
  t: TFunction,
  now = Date.now()
): string => {
  const expiry = getCampaignExpiryTimestamp(campaign)

  // EXSR-8094: a flat item discount with no end date reads "Unlimited"; every other
  // campaign (percentage item discounts included) keeps showing "--" for no end date.
  if (expiry == null && isFlatItemDiscount(campaign)) {
    return t('marketing.campaigns.unlimited')
  }

  if (expiry == null || expiry < now) {
    return '--'
  }

  const daysLeft = Math.max(1, Math.ceil((expiry - now) / MS_PER_DAY))
  return t('marketing.campaigns.days-left', { count: daysLeft })
}

export const getCampaignTypeLabel = (
  campaignType: CampaignType,
  t: TFunction
): string => {
  const keyMap: Record<CampaignType, string> = {
    'food-offer': 'marketing.campaigns.types.food-offer',
    cart_discount: 'marketing.campaigns.types.cart-discount',
    buyXGetYOffer: 'marketing.campaigns.types.buy-x-get-y-default'
  }

  return t(keyMap[campaignType])
}

export const getCampaignDetailsStatusLabel = (
  campaign: Campaign,
  t: TFunction,
  now = Date.now()
): string => {
  const displayStatus = getCampaignDisplayStatus(campaign, now)
  return t(`marketing.campaigns.status.${displayStatus}`)
}

export const getCampaignDetailsDiscountLabel = (
  campaign: Campaign,
  t: TFunction,
  currency = ''
): string => {
  if (campaign.campaignType === 'cart_discount' && campaign.amountPercentage != null) {
    return t('marketing.campaigns.percent-off', {
      value: campaign.amountPercentage
    })
  }

  if (campaign.campaignType === 'food-offer' && campaign.offerAmount != null) {
    if (isFlatItemDiscount(campaign)) {
      return getFlatAmountOffLabel(campaign.offerAmount, currency, t) ?? '--'
    }

    return t('marketing.campaigns.percent-off', {
      value: campaign.offerAmount
    })
  }

  if (campaign.campaignType === 'buyXGetYOffer') {
    if (
      campaign.offerType === 'FREE_SAME' ||
      campaign.offerType === 'FREE_DIFF'
    ) {
      return t('marketing.campaigns.percent-off', { value: 100 })
    }

    const discountPercent = campaign.itemToGet?.discountPercent
    if (discountPercent != null) {
      return t('marketing.campaigns.percent-off', { value: discountPercent })
    }
  }

  return '--'
}

export const getCampaignDetailsExpirationLabel = (
  campaign: Campaign,
  t: TFunction,
  now = Date.now()
): string => getCampaignExpirationText(campaign, t, now)

export const formatCampaignCurrencyAmount = (
  value: number | null | undefined,
  currency: string
): string => {
  if (value == null) return '--'

  return formatPrice(value, currency, {
    signDisplay: 'never'
  })
}

/** "100 DZD off" (EXSR-8094): the flat amount a flat item discount takes off. */
export const getFlatAmountOffLabel = (
  amount: number | null | undefined,
  currency: string,
  t: TFunction
): string | null => {
  if (amount == null) return null

  return t('marketing.campaigns.amount-off', {
    value: formatCampaignCurrencyAmount(amount, currency)
  })
}

/** The pill next to an offer's name in the list: "20% off", or "100 DZD off" for a flat item discount. */
export const getCampaignDiscountBadge = (
  campaign: Campaign,
  t: TFunction,
  currency = ''
): string | null => {
  if (isItemDiscount(campaign) && isFlatItemDiscount(campaign)) {
    return getFlatAmountOffLabel(campaign.offerAmount, currency, t)
  }

  return getCampaignPercentageBadge(campaign, t)
}

export const getCampaignItemPriceLabel = (
  item: CampaignItemDetail | undefined,
  currency: string,
  t: TFunction
): { primary: string; secondary?: string } => {
  if (!item) {
    return { primary: '--' }
  }

  if (item.isFree) {
    return { primary: t('marketing.campaigns.details.free') }
  }

  if (item.discountedPrice != null && item.originalPrice != null) {
    return {
      primary: formatCampaignCurrencyAmount(item.discountedPrice, currency),
      secondary: formatCampaignCurrencyAmount(item.originalPrice, currency)
    }
  }

  if (item.price != null) {
    return { primary: formatCampaignCurrencyAmount(item.price, currency) }
  }

  return { primary: '--' }
}
